import './Attachments.sass';

import React, { useMemo, useState } from 'react';
import { useSelector } from 'react-redux';

import {
  BattleAttach,
  BundleMsgAttach,
  Collage,
  InlineVideo,
  Post,
  RouletteLotAttach,
  TipsAttach,
} from '$shared/components';
import { LinkAttach } from '$shared/components/LinkAttach/LinkAttach';
import { postsSelector } from '$store/posts';
import { classNames } from '$utils';
import { useCurrentUserInfo, useTranslator } from '$hooks';
import { AttachmentsProps } from '$shared/components/Attachments/Attachments.interface';
import {
  AttachBattlePrize,
  AttachRoulette,
  AttachRouletteLot,
  AttachType,
} from '$store/models';
import { Button } from '$uikit';

export function Attachments({
  attachments,
  photosWidth = 600,
  parentObject = 'default',
  canExpand = false,
  forceBlur = false,
  canUnblur = false,
  decryptKey = '',
  isHidden = false,
  ratio = 1,
  isBlocked,
  price,
  isVisible = false,
  adId = 0,
}: AttachmentsProps) {
  const { posts } = useSelector(postsSelector);
  const t = useTranslator();
  const user = useCurrentUserInfo();

  const [isBlurred, setIsBlurred] = useState(forceBlur);

  function handleUnblurButtonClick(e) {
    e.stopPropagation();
    setIsBlurred(false);
  }

  const [roulette, rouletteLot, battlePrize] = useMemo(() => {
    let roulette: AttachRoulette | null = null;
    let rouletteLot: AttachRouletteLot | null = null;
    let battle: AttachBattlePrize | null = null;
    if (!attachments) {
      return [null, null, null];
    }
    for (let item of attachments) {
      if (item.type === AttachType.ROULETTE && item.roulette) {
        roulette = item.roulette;
        break;
      } else if (item.type === AttachType.ROULETTE_LOT && item.rouletteLot) {
        rouletteLot = item.rouletteLot;
        break;
      } else if (item.type === AttachType.BATTLE && item.battlePrize) {
        battle = item.battlePrize;
        break;
      }
    }
    return [roulette, rouletteLot, battle];
  }, [attachments]);

  const bundle = useMemo(() => {
    if (!attachments) return null;
    for (let item of attachments) {
      if (item.type === AttachType.BUNDLE) {
        return item.bundle;
      }
    }
  }, [attachments]);

  const link = useMemo(() => {
    if (!attachments) {
      return null;
    }

    for (let item of attachments) {
      if (item.type === 'link') {
        return item.link;
      }
    }

    return null;
  }, [attachments]);

  const tips = useMemo(() => {
    if (!attachments) {
      return null;
    }

    for (let item of attachments) {
      if (item.type === AttachType.TIPS) {
        return item.tips;
      }
    }

    return null;
  }, [attachments]);

  const media = useMemo(() => {
    return attachments
      ? attachments.filter((item) => item.type === 'photo' || item.type === 'video')
      : [];
  }, [attachments]);

  if (!attachments || !attachments.length) {
    return null;
  }

  function renderMedia() {
    if (!media.length || bundle) {
      return null;
    }

    if (media.length === 1 && media[0].type === 'video') {
      const video = media[0].video;
      if (video) {
        return (
          <InlineVideo
            video={video}
            parentObject={parentObject}
            decryptKey={decryptKey}
            isHidden={isHidden}
            maxWidth={photosWidth}
            isBlocked={isBlocked}
            isVisible={isVisible}
          />
        );
      }
    }

    // if (user.id === 1 && media.length > 1) {
    //   return (
    //     <PostMediaGroup
    //       items={media}
    //       decryptKey={decryptKey}
    //       isHidden={isHidden}
    //       isVisible={isVisible}
    //       isBlocked={!!isBlocked}
    //     />
    //   );
    // }

    return (
      <Collage
        items={media}
        maxWidth={photosWidth}
        decryptKey={decryptKey}
        isHidden={isHidden}
        ratio={ratio}
        isBlocked={isBlocked}
        isVisible={isVisible}
        cropTen={true}
      />
    );
  }

  if (
    canExpand &&
    media.length === 0 &&
    link &&
    link.type === 'post' &&
    posts[link.object.id]
  ) {
    return (
      <div className="Post__repost">
        <div className="LinkAttach__type">{t('post_repost')}</div>
        <Post postId={link.object.id} isReport />
      </div>
    );
  }

  return (
    <div className="Attachments">
      <div
        className={classNames({
          Attachments__media_wrap: true,
          blur: isBlurred,
        })}
      >
        <div className="Attachments__media">{renderMedia()}</div>

        {isBlurred && canUnblur && media.length > 0 && (
          <div className={'Attachments__media__unblur_button_wrap'}>
            <Button onClick={handleUnblurButtonClick}>
              {t('message_show_content_button')}
            </Button>
          </div>
        )}
      </div>
      {link && (
        <LinkAttach
          link={link}
          mini={media.length > 0}
          parentObject={parentObject}
          attachmentsLength={attachments.length}
          adId={adId}
        />
      )}
      {tips && <TipsAttach tips={tips} />}
      {rouletteLot && price && (
        <RouletteLotAttach price={price} rouletteLot={rouletteLot} />
      )}
      {battlePrize && <BattleAttach battlePrize={battlePrize} />}
      {bundle && <BundleMsgAttach attachments={attachments} bundle={bundle} />}
    </div>
  );
}
